import React from 'react';
import { Button, Tag } from 'antd';
import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import CommentModal from '../Forms/CommentModal';
import { LocationItem } from '../Forms/ItemsEditor';
import { getLabelColor } from '../../Utils/RouteDetailsUtils';
import { RouteTableRow } from '../../Types/RouteDetails.types';

const VALUE_TYPE_CONFIG: Record<string, { color: string; tagColor: string; label: string }> = {
  cash: { color: '#52c41a', tagColor: 'green', label: 'Cash' },
  cheque: { color: '#fa8c16', tagColor: 'orange', label: 'Cheque' },
  unit: { color: '#1890ff', tagColor: 'blue', label: 'Units' },
};

const getTypeConfig = (type: string) =>
  VALUE_TYPE_CONFIG[type.toLowerCase()] ?? {
    color: '#888',
    tagColor: 'default',
    label: type.charAt(0).toUpperCase() + type.slice(1),
  };

interface GetRouteColumnsArgs {
  onDownload: (url: string, fileName: string) => void;
  onSaveComment: (locationId: number, comment: string) => void;
}

const getRouteColumns = ({ onDownload, onSaveComment }: GetRouteColumnsArgs): ColumnsType<RouteTableRow> => [
  {
    title: 'Address',
    dataIndex: 'address',
    key: 'address',
    width: '25%',
    defaultSortOrder: 'ascend' as const,
    sorter: (a, b) => a.waypointIndex - b.waypointIndex,
    render: (_: any, record: RouteTableRow) => {
      const { location, displayLabel } = record;
      const isStart = displayLabel === 'Start';
      const isEnd = displayLabel === 'End';
      const color = getLabelColor(displayLabel);
      return (
        <span>
          {color && (
            <span style={{ color, fontWeight: 600, marginRight: 4 }}>
              {(isStart || isEnd) && <CheckCircleOutlined style={{ marginRight: 4 }} />}
              {isStart ? 'Start:' : 'End:'}
            </span>
          )}
          {location.input_address}
        </span>
      );
    },
  },
  {
    title: 'Location Name',
    dataIndex: 'locationName',
    key: 'locationName',
    width: '16%',
    render: (value: string) => <span>{value || 'N/A'}</span>,
  },
  {
    title: 'Items',
    key: 'items',
    dataIndex: 'items',
    width: '16%',
    render: (items: LocationItem[]) => {
      if (!items || items.length === 0) return <span style={{ color: '#aaa' }}>-</span>;
      return (
        <div>
          {items.map((item) => (
            <div key={`${item.item_name}-${item.operation}-${item.capacity}`}>
              <span style={{ color: item.operation === 'pickup' ? '#52c41a' : '#ff4d4f' }}>
                {item.operation === 'pickup' ? '↑' : '↓'}
              </span>{' '}
              {item.item_name} <span>({item.capacity})</span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    title: 'Value',
    dataIndex: 'value',
    key: 'value',
    width: '14%',
    render: (value: number) => <span>{value ?? 0}</span>,
  },
  {
    title: 'Delivered Value',
    key: 'deliveredValue',
    dataIndex: 'deliveredValue',
    width: '18%',
    render: (_: any, record: RouteTableRow) => {
      if (record.deliveryStatus !== 'delivered') {
        return <span style={{ color: '#aaa' }}>—</span>;
      }
      const valueEntriesObj: Record<string, number> = record.valueEntries ?? {};
      const entries = Object.entries(valueEntriesObj);
      if (entries.length === 0) {
        return <span>{Number(record.deliveredValue ?? 0).toFixed(2)}</span>;
      }
      if (entries.length === 1) {
        const [type, amount] = entries[0];
        const cfg = getTypeConfig(type);
        return (
          <div>
            <Tag color={cfg.tagColor}>{cfg.label}</Tag>
            <span style={{ color: cfg.color }}>{Number(amount).toFixed(2)}</span>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {entries.map(([type, amount]) => {
            const cfg = getTypeConfig(type);
            return (
              <div key={type}>
                <Tag color={cfg.tagColor} style={{ marginRight: 4 }}>
                  {cfg.label}
                </Tag>
                <span style={{ color: cfg.color }}>{Number(amount).toFixed(2)}</span>
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid #eee', marginTop: 2, paddingTop: 2, fontSize: 11, color: '#888' }}>
            Total: <span style={{ color: '#333' }}>{Number(record.deliveredValue ?? 0).toFixed(2)}</span>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Delivery Return',
    key: 'deliveryReturn',
    dataIndex: 'deliveryReturn',
    width: '13%',
    render: (_: any, record: RouteTableRow) => {
      if (record.deliveryStatus !== 'delivered') {
        return <span style={{ color: '#aaa' }}>—</span>;
      }
      const value = Number(record.value ?? 0);
      const delivered = Number(record.deliveredValue ?? 0);
      const returnValue = value - delivered;

      return <span style={{ color: '#f5222d', fontWeight: 600 }}>{returnValue.toFixed(2)}</span>;
    },
  },
  {
    title: 'Delivery Status',
    dataIndex: 'deliveryStatus',
    key: 'deliveryStatus',
    width: '12%',
    render: (value: string) => {
      const colorMap: Record<string, string> = {
        unplanned: 'default',
        planned: 'blue',
        delivered: 'green',
        skipped: 'orange',
        cancelled: 'red',
      };
      return <Tag color={colorMap[value] ?? 'default'}>{value || 'unplanned'}</Tag>;
    },
  },
  {
    title: 'Delivery Date',
    dataIndex: 'deliveryDateTime',
    key: 'deliveryDateTime',
    width: '18%',
    sorter: (a, b) => {
      if (!a.deliveryDateTime) return 1;
      if (!b.deliveryDateTime) return -1;
      return moment(a.deliveryDateTime).valueOf() - moment(b.deliveryDateTime).valueOf();
    },
    render: (value: string) =>
      value && moment(value).isValid() ? (
        <div>
          <div>{moment(value).format('YYYY-MM-DD')}</div>
          <div>{moment(value).format('HH:mm:ss')}</div>
        </div>
      ) : (
        '-'
      ),
  },
  {
    title: 'S.R.N Value',
    dataIndex: 'srnValue',
    key: 'srnValue',
    width: '12%',
    render: (value: string) => <span>{value || '-'}</span>,
  },
  {
    title: 'Delivery Remark',
    dataIndex: 'deliveryRemark',
    key: 'deliveryRemark',
    width: '15%',
    render: (value: string) => <span>{value || '-'}</span>,
  },
  {
    title: 'Invoice',
    dataIndex: 'filesForLocation',
    key: 'filesForLocation',
    width: '15%',
    render: (files: any[], record: RouteTableRow) =>
      files && files.length > 0 ? (
        <>
          {files.map((file) => (
            <div key={file.file_key} className="mg-b-4">
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => onDownload(file.url, file.file_key)}
                className="pd-0 mg-r-8"
              >
                Download
              </Button>
              <span className={record.isStart || record.isEnd ? 'fw-600' : ''}>{file.file_key}</span>
            </div>
          ))}
        </>
      ) : (
        <span className="fs-italic c-888">No invoice</span>
      ),
  },
  {
    title: 'Comment',
    key: 'comment',
    dataIndex: 'comment',
    width: '15%',
    render: (_: any, record: RouteTableRow) => {
      const { location, comment } = record;
      return (
        <div className="route-details__comment-cell">
          <CommentModal
            address={location?.input_address ?? ''}
            existingComment={comment ?? null}
            onSave={(newComment) => onSaveComment(location.location_id, newComment)}
          />
        </div>
      );
    },
  },
];

export default getRouteColumns;
